import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CreateRequest } from 'gql/Request';
import { useTranslation } from 'react-i18next';
import { Well } from '@tanium/react-well';
import { Stack } from '@tanium/react-stack';
import Helper from 'utils/Helper';
import { Box } from '@tanium/react-box';
import MediumBoldText from 'components/styles/text/MediumBoldText';
import SmallLightText from 'components/styles/text/SmallLightText';
import { defineColor } from '@tanium/react-theme-definition';
import { useTheme } from '@tanium/react-theme-context';
import { HEX_E2E8F0 } from '@tanium/colors';
import { Button } from '@tanium/react-button';
import { TrashIcon } from '@tanium/react-graphics';
import { TooltippedIconButton } from '@tanium/react-icon-button';
import RequestSelectors from 'components/pages/request/store/RequestSelectors';
import RequestSlice from 'components/pages/request/store/RequestSlice';
import { DefinitionList, DefinitionListItem } from '@tanium/react-definition-list';
import IAccessTypes from 'common/IAccessTypes';
import { FormRow } from '@tanium/react-form';
import { Formik, Form, Field, FieldProps } from 'formik';
import { FormTextInput } from '@tanium/react-form-text-input';
import { FormTextareaInput } from '@tanium/react-form-textarea-input';
import {
  TICKET_INFO,
  DETAILS,
} from 'components/pages/request/requestPartials/createRequestFieldsPartials/RequestDetails';

export const RECENT_REQUESTS_KEY = 'recent-requests';

const WellBorder = defineColor([HEX_E2E8F0, '#587c89']);
const WellBackground = defineColor(['#f5f5f5', '#222e41']);
const AccessTypeBackground = defineColor(['rgb(255 255 255 / 50%)', 'rgb(24 34 46 / 50%)']);
const AccessTypeBackgroundBorder = defineColor(['#fff', '#18222e']);

export const RecentRequests: React.VFC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const createProcessing = useSelector(RequestSelectors.selectCreateProcessing);
  const { localStorage } = window;
  const [recentRequests, setRecentRequests] = useState(
    Helper.safelyParseJSON(localStorage.getItem(RECENT_REQUESTS_KEY))
  );
  const subtextColor = theme.colors.subtle;

  const handleSubmit = (values: CreateRequest): void => {
    const createRequest: CreateRequest = {
      targetType: values.targetType,
      targetId: values.targetId,
      ticketInfo: values.ticketInfo,
      details: values.details,
      access: values.access,
      autopage: values.autopage,
    };
    dispatch(RequestSlice.actions.createNewRequest(createRequest));
  };

  const deleteRecentRequest = (index: number): void => {
    const recentRequestsArray = Helper.safelyParseJSON(localStorage.getItem(RECENT_REQUESTS_KEY));
    // This if() is unnecessary unless someone dumps the local storage while in the page, but AI recommended it, and it's a good idea
    /* istanbul ignore next */
    if (Array.isArray(recentRequestsArray)) {
      recentRequestsArray.splice(index, 1);
      localStorage.setItem(RECENT_REQUESTS_KEY, JSON.stringify(recentRequestsArray));
      setRecentRequests(recentRequestsArray);
    }
  };

  return (
    <div>
      {Array.isArray(recentRequests) && recentRequests.length ? (
        recentRequests?.map((request: CreateRequest & { targetName: string | undefined }, i: number) => (
          <Box key={`recent-request-${i}`} m={1}>
            <Box
              position={'relative'}
              display={'flex'}
              justifyContent={'flex-end'}
              height={0}
              top={'0.5em'}
              right={'0.5em'}
            >
              <TooltippedIconButton
                data-testid={'recent-request-delete'}
                style={{ marginLeft: '0.5em' }}
                tooltipContent={''}
                label={''}
                height={16}
                width={16}
                icon={TrashIcon}
                onClick={() => {
                  deleteRecentRequest(i);
                }}
              />
            </Box>
            <Formik
              initialValues={{
                targetType: request.targetType,
                targetId: request.targetId,
                ticketInfo: '',
                details: '',
                access: request.access,
                autopage: false,
              }}
              onSubmit={handleSubmit}
            >
              <Form>
                <Well
                  borderLeft={'0.2em solid'}
                  borderLeftColor={WellBorder(theme)}
                  backgroundColor={WellBackground(theme)}
                  borderRadius={'0.3em'}
                >
                  <Stack direction="column" itemSpacing={0.5} mb={1}>
                    <Box display="inline-flex">
                      <Box mr={1}>{t(request.targetType)}</Box>
                    </Box>
                    <Box color={subtextColor} fontSize={13}>
                      {request.targetName && <> {request.targetName} | </>}
                      {request.targetId}
                    </Box>
                    {request.access.map((access, j) => (
                      <Well
                        key={`recent-request-${i}-${j}`}
                        backgroundColor={AccessTypeBackground(theme)}
                        border={'0.1em solid'}
                        borderColor={AccessTypeBackgroundBorder(theme)}
                        borderRadius={'0.3em'}
                      >
                        <DefinitionList>
                          {Object.entries(access).map(([key, value]) => (
                            <DefinitionListItem
                              key={`recent-request-${i}-${j}-${key}`}
                              term={<Box fontSize={12}>{key}:</Box>}
                              description={<Box fontSize={12}>{value in IAccessTypes ? t(value) : value}</Box>}
                            />
                          ))}
                        </DefinitionList>
                      </Well>
                    ))}
                  </Stack>
                  <Field name={TICKET_INFO}>
                    {({ form }: FieldProps): React.ReactElement => (
                      <Box mb={2}>
                        <FormRow label={<Box fontSize={12}>{t('TICKET_INFO')}</Box>}>
                          <Box display={'flex'}>
                            <FormTextInput
                              id="create-recent-request-ticket-info"
                              data-testid="ticketInfo"
                              placeholder={t('TICKET_INFO_PLACEHOLDER')}
                              value={request.ticketInfo}
                              onInput={(event) => {
                                const { value } = event.target as any;
                                void form.setFieldValue(TICKET_INFO, value);
                              }}
                            />
                          </Box>
                        </FormRow>
                      </Box>
                    )}
                  </Field>
                  <Field name={DETAILS}>
                    {({ form }: FieldProps): React.ReactElement => (
                      <FormRow label={<Box fontSize={12}>{`${t('JUSTIFICATION')}*`}</Box>}>
                        <Box display={'flex'}>
                          <FormTextareaInput
                            id="create-recent-request-details"
                            data-testid="justification"
                            placeholder={t('JUSTIFICATION_PLACEHOLDER')}
                            value={request.details}
                            required={true}
                            onInput={(event) => {
                              const { value } = event.target as any;
                              void form.setFieldValue(DETAILS, value);
                            }}
                          />
                        </Box>
                      </FormRow>
                    )}
                  </Field>
                  <Button variant="secondary" disabled={createProcessing} data-testid="create-recent-request">
                    {t('REQUEST_ACCESS')}
                  </Button>
                </Well>
              </Form>
            </Formik>
          </Box>
        ))
      ) : (
        <Box pt={2} mx={2}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MediumBoldText>{t('NO_RECENT_REQUEST')}</MediumBoldText>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
            <SmallLightText>{t('NO_RECENT_REQUEST_DESCRIPTION')}</SmallLightText>
          </div>
        </Box>
      )}
    </div>
  );
};

export default RecentRequests;
